import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Extend the Window interface to include gtag
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, any>
    ) => void;
  }
}

/**
 * Custom hook to track page views with Google Analytics
 * Automatically sends a page view event when the route changes
 */
export const usePageTracking = (): void => {
  const location = useLocation();

  useEffect(() => {
    // Check if gtag is available (it should be loaded from the script in index.html)
    if (typeof window.gtag !== 'undefined') {
      // Send page view to Google Analytics
      window.gtag('config', 'G-C0HQ3CZK42', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
      });

      // Optional: Log page views in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Page view:', location.pathname + location.search);
      }
    }
  }, [location]);
};

/**
 * Track custom events to Google Analytics
 * @param eventName - The name of the event (e.g., 'button_click', 'form_submit')
 * @param eventParams - Additional parameters for the event
 *
 * Example usage:
 * trackEvent('post_created', { category: 'engagement', label: 'new_post' });
 */
export const trackEvent = (
  eventName: string,
  eventParams: Record<string, any> = {}
): void => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, eventParams);

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Event:', eventName, eventParams);
    }
  }
};
